import { format as formatDate } from 'date-fns';
import { cloneElement, ReactElement } from 'react';
import useFindStoresQuery from '@/api/queries/useFindStoresQuery';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { SearchProps } from '@/components/search/useSearch';
import StatusActiveIndicator from '@/components/StatusActiveIndicator';
import { isStatusActive } from '@/models/Status';
import { StoreAssociation } from '@/models/Store';
import { RetailerStoreEditPath, StoreEditPath } from '@/modules/admin/paths';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface Store {
  id: string;
  organizationId: string;
  retailerId: string;
}

interface StoreTableProps extends SearchProps {
  retailerId?: string;
  onSelectStoreElement?: ReactElement;
  onSelectStore?: (store: Store) => void;
  onSelectStoreLabel?: string;
}

const StoreTable = ({
  searchQuery,
  page = 1,
  onChangePage = () => {},
  retailerId,
  onSelectStoreElement,
  onSelectStore = () => {},
  onSelectStoreLabel = 'Select store',
}: StoreTableProps) => {
  const showRetailerColumn = !retailerId;
  const { stores, isLoading, itemsPerPage } = useFindStoresQuery(
    page,
    searchQuery,
    {
      retailerId,
    },
    10,
    showRetailerColumn ? [StoreAssociation.RETAILER] : undefined,
  );

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''}>
          <thead>
            <tr>
              <th>Name</th>
              {showRetailerColumn && <th>Retailer</th>}
              <th>Created At</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && stores.items.length === 0 ? (
              <tr>
                <td colSpan={showRetailerColumn ? 5 : 4} className="text-center py-6 text-gray-500">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" aria-hidden="true" />
                    <span>No stores found</span>
                  </div>
                </td>
              </tr>
            ) : (
              stores.items.map((store) => {
                if (store.id === undefined) {
                  return null;
                }

                const editPath = retailerId
                  ? RetailerStoreEditPath.withParams(retailerId, store.id)
                  : StoreEditPath.withId(store.id);

                return (
                  <tr key={store.id}>
                    <td>
                      <Link to={editPath} className="text-blue-500 hover:underline">
                        {store.name}
                      </Link>
                    </td>
                    {showRetailerColumn && <td>{store.retailer?.name || ''}</td>}
                    <td>{store.createdAt && formatDate(store.createdAt, 'd MMM yyyy')}</td>
                    <td>
                      <StatusActiveIndicator isActive={isStatusActive(store)} />
                    </td>
                    <td>
                      {onSelectStoreElement ? (
                        cloneElement(onSelectStoreElement, {
                          onClick: () => onSelectStore(store),
                        })
                      ) : (
                        <Button onClick={() => onSelectStore(store)} variant="secondary" size="xs">
                          {onSelectStoreLabel}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {isLoading && (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={stores.totalCount || 0}
          estimatedTotalCount={stores.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default StoreTable;
